import { Grid } from '../../core/Grid.js';
import {
    Position,
    positionKey,
    parsePositionKey,
} from '../../core/GridGeometry.js';
import { SplittingGame } from './SplittingGame.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class SplittingVortex extends SplittingGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.cachedSimulation = null;
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            // Splitting-specific: no splitter on either gate cell
            if (this.hasSplitterAt(p)) return false;
            if (this.hasSplitterAt(paired)) return false;
            // No digit on either gate cell
            if (this.getGrid().hasDigitAt(p)) return false;
            if (this.getGrid().hasDigitAt(paired)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.cachedSimulation = null;
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Placement Blocking ────────────────────────────────────────

    override toggleSplitter(pos: Position): void {
        // Block splitter placement on active vortex gate cell
        if (this.gm.isActiveGate(pos)) return;
        super.toggleSplitter(pos);
    }

    // ── Teleportation ─────────────────────────────────────────────

    protected override checkVortexTeleport(position: Position): Position | null {
        if (!this.gm.hasActiveGates()) return null;

        const key = positionKey(position);
        const pairedKey = this.gm.raw.get(key);
        if (pairedKey) {
            return parsePositionKey(pairedKey);
        }
        return null;
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.cachedSimulation = null;
    }
}
